/**
 * Where the assistant is showing.
 *
 * The assistant has two presentations and **one conversation**: a drawer over
 * whatever page you are on, and a full-width section of its own. Rather than
 * mount the component twice and try to keep two transcripts in step, there is a
 * single `<assistant-surface>` at the application root whose chrome changes.
 * The transcript never moves, so opening the full view from the drawer
 * continues the thread you were in the middle of.
 *
 * `closed` is not "no assistant" - the element stays mounted and keeps its
 * state, exactly like the sections do, so a chat survives being dismissed.
 */

import {
  Atom,
  activeSection,
  assistantTab,
  isRootUser,
  type AssistantTab,
  type Section,
} from './store.ts';
import { assistantChiefEngineer } from './assistant-settings.ts';
import { isPageVisible } from './page-visibility.ts';
import { syncToUrl } from './url-sync.ts';

export type AssistantSurface = 'closed' | 'drawer' | 'mark' | 'full';

/** True while the drawer is pulled out over another section. */
export const assistantDrawerOpen = new Atom<boolean>(false);

/**
 * The presentation to render. The section wins: while you are *on* the
 * assistant, a drawer over it would be the same conversation twice.
 *
 * `mark` is the drawer shrunk to the compass rose in the corner - still open,
 * still holding the thread, just out of the way of whatever is underneath.
 */
export function assistantSurface(): AssistantSurface {
  if (activeSection.value === 'assistant') return 'full';
  if (!assistantDrawerOpen.value) return 'closed';
  return assistantMinimized.value ? 'mark' : 'drawer';
}

/* ── How wide, and whether it is shrunk ────────────────────

   The drawer overlays the page rather than pushing it - the guidance is to
   animate state, not layout, and a dashboard that reflows every time a question
   occurs to someone is the layout moving. Width is therefore free: it costs the
   page underneath nothing, so it is draggable, and remembered. */

const WIDTH_KEY = 'neptune_assistant_drawer_width_v1';
const MIN_KEY = 'neptune_assistant_minimized_v1';

/** The drawer never gets narrower than a readable message or wider than most
 *  of the window; both ends are clamped again on every drag. */
export const DRAWER_MIN = 320;
export const DRAWER_DEFAULT = 480;

export function drawerMax(): number {
  if (typeof window === 'undefined') return 900;
  return Math.max(DRAWER_MIN, Math.round(window.innerWidth * 0.8));
}

function readWidth(): number {
  try {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    if (!Number.isFinite(stored) || stored <= 0) return DRAWER_DEFAULT;
    return Math.min(Math.max(stored, DRAWER_MIN), drawerMax());
  } catch {
    return DRAWER_DEFAULT;
  }
}

export const assistantDrawerWidth = new Atom<number>(readWidth());

export function setAssistantDrawerWidth(px: number): void {
  const clamped = Math.min(Math.max(Math.round(px), DRAWER_MIN), drawerMax());
  assistantDrawerWidth.set(clamped);
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--assistant-drawer-w', `${clamped}px`);
  }
  try {
    localStorage.setItem(WIDTH_KEY, String(clamped));
  } catch {
    // Not worth failing a drag over; the width simply does not stick.
  }
}

function readMinimized(): boolean {
  try {
    return localStorage.getItem(MIN_KEY) === '1';
  } catch {
    return false;
  }
}

export const assistantMinimized = new Atom<boolean>(readMinimized());

export function setAssistantMinimized(on: boolean): void {
  assistantMinimized.set(on);
  syncAssistantSurface();
  try {
    localStorage.setItem(MIN_KEY, on ? '1' : '0');
  } catch {
    // As above.
  }
}

/** Reflect the surface onto the body, for the CSS that draws the chrome. */
export function syncAssistantSurface(): void {
  if (typeof document === 'undefined') return;
  document.body.dataset.assistant = assistantSurface();
  const root = document.documentElement.style;
  root.setProperty('--assistant-drawer-w', `${assistantDrawerWidth.value}px`);
  root.setProperty(
    '--assistant-drawer-h',
    assistantDrawerHeight.value === 0 ? '100%' : `${assistantDrawerHeight.value}px`,
  );
  root.setProperty('--assistant-rail-w', `${assistantRailWidth.value}px`);
  if (assistantFloating.value) document.body.dataset.assistantFloat = '1';
  else delete document.body.dataset.assistantFloat;
  const at = assistantFloatAt.value;
  root.setProperty('--assistant-float-x', `${at.x}px`);
  root.setProperty('--assistant-float-y', `${at.y}px`);
}

export function openAssistantDrawer(): void {
  if (!isPageVisible('assistant')) return;
  assistantDrawerOpen.set(true);
  setAssistantMinimized(false);
  syncAssistantSurface();
}

export function closeAssistantDrawer(): void {
  assistantDrawerOpen.set(false);
  syncAssistantSurface();
}

export function toggleAssistantDrawer(): void {
  if (assistantDrawerOpen.value) closeAssistantDrawer();
  else openAssistantDrawer();
}

/**
 * The section to come back to when the full view is dismissed.
 *
 * Leaving the assistant is a return, not a destination: you were reading a
 * dashboard when the question occurred to you, and that is where "done" means.
 * Explore is the fallback, as it is everywhere else in the app.
 */
let cameFrom: Section = 'explore';

/** Switch to the full-width assistant section, remembering where from. */
export function openAssistantSection(): void {
  if (!isPageVisible('assistant')) return;
  if (activeSection.value !== 'assistant') cameFrom = activeSection.value;
  assistantDrawerOpen.set(false);
  activeSection.set('assistant');
  document.body.dataset.section = 'assistant';
  syncAssistantSurface();
  syncToUrl();
}

/** Leave the full view for wherever the assistant was opened from. */
export function leaveAssistantSection(): void {
  if (activeSection.value !== 'assistant') return;
  activeSection.set(cameFrom);
  document.body.dataset.section = cameFrom;
  syncAssistantSurface();
  syncToUrl();
}

/* ── Simple and Technical ──────────────────────────────────

   Simple is the default and the whole point: tool activity, token and cost
   readouts and the model's reasoning summary are plumbing, and a chief engineer
   asking about fuel should not have to read around them. Technical puts them
   all back for whoever is debugging a proposal.

   Stored in this browser for now. `docs/plans/assistant.md` has it promoting to
   `cfg.user_preference` in B1, where the migration that would carry it is
   already being written - and it needs a no-database answer either way, which
   is Simple. */

const TECHNICAL_KEY = 'neptune_assistant_technical_v1';

function readTechnical(): boolean {
  try {
    return localStorage.getItem(TECHNICAL_KEY) === '1';
  } catch {
    return false;
  }
}

export const assistantTechnical = new Atom<boolean>(readTechnical());

export function setAssistantTechnical(on: boolean): void {
  assistantTechnical.set(on);
  try {
    localStorage.setItem(TECHNICAL_KEY, on ? '1' : '0');
  } catch {
    // A browser refusing storage is not worth failing a click over; the
    // choice simply does not stick.
  }
}

/* ── The section, and what is in it ────────────────────────

   The assistant is a place as well as an action: `ASSISTANT` in the navigation
   opens it, `ASK` in the utilities opens the drawer over whatever you are on.
   A place needs room to grow - usage and cost land with B1, and whatever comes
   after that - so the section carries its own tab row, the way Explore does,
   rather than being a single screen.

   The label is a constant because the AI area is getting a real product name
   and the rename should be one line rather than a search across the app. */

export const ASSISTANT_SECTION_LABEL = 'SONAR';

export { assistantTab };
export type { AssistantTab };

export function setAssistantTab(tab: AssistantTab): void {
  if (assistantTab.value === tab) return;
  assistantTab.set(tab);
  if (typeof document !== 'undefined') document.body.dataset.assistantTab = tab;
  syncToUrl();
}

/* ── How tall, and how wide the page's rail is ─────────────

   The drawer is anchored to the bottom-right corner, so shrinking it downwards
   leaves the composer where your hands already are and uncovers the top of the
   page - which is the half you are usually reading. Height 0 means "all of it".
   The default is 80% of the window, so the drawer opens looking like a panel
   you can resize rather than a wall; `v2` because v1 stored "all of it" as the
   default, and keeping it would hide the new one from everyone who ever opened
   the drawer. */

const HEIGHT_KEY = 'neptune_assistant_drawer_height_v2';
const RAIL_KEY = 'neptune_assistant_rail_width_v1';

export const DRAWER_MIN_H = 260;
export const RAIL_MIN = 190;
export const RAIL_DEFAULT = 288;

function headerHeight(): number {
  if (typeof document === 'undefined') return 56;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--app-header-h');
  const px = Number.parseInt(raw, 10);
  return Number.isFinite(px) ? px : 56;
}

/** The tallest the drawer can be: everything under the header. */
export function drawerMaxHeight(): number {
  if (typeof window === 'undefined') return 900;
  return Math.max(DRAWER_MIN_H, window.innerHeight - headerHeight());
}

export function railMax(): number {
  if (typeof window === 'undefined') return 520;
  return Math.max(RAIL_MIN, Math.min(560, Math.round(window.innerWidth * 0.4)));
}

function readNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    // `Number(null)` is 0, not NaN - so a missing key read as a stored zero and
    // collapsed the rail to nothing. An absent value is the default; only a
    // value that is actually there is allowed to be zero.
    if (raw === null) return fallback;
    const stored = Number(raw);
    return Number.isFinite(stored) && stored >= 0 ? stored : fallback;
  } catch {
    return fallback;
  }
}

/** The height a drawer opens at before anyone drags it. */
export function drawerDefaultHeight(): number {
  if (typeof window === 'undefined') return 0;
  return Math.min(drawerMaxHeight(), Math.max(DRAWER_MIN_H, Math.round(window.innerHeight * 0.8)));
}

/** 0 means full height - not a magic number anyone types. */
export const assistantDrawerHeight = new Atom<number>(readNumber(HEIGHT_KEY, drawerDefaultHeight()));

export function setAssistantDrawerHeight(px: number): void {
  const full = drawerMaxHeight();
  const wanted = Math.round(px);
  // Snap back to "all of it" near the top, so dragging up returns to the state
  // you started in rather than to 99% of it.
  const clamped = wanted >= full - 24 ? 0 : Math.min(Math.max(wanted, DRAWER_MIN_H), full);
  assistantDrawerHeight.set(clamped);
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty(
      '--assistant-drawer-h',
      clamped === 0 ? '100%' : `${clamped}px`,
    );
  }
  try {
    localStorage.setItem(HEIGHT_KEY, String(clamped));
  } catch {
    // Not worth failing a drag over.
  }
}

/** The chat rail on the full page, which drags like Explore's sidebar. */
export const assistantRailWidth = new Atom<number>(readNumber(RAIL_KEY, RAIL_DEFAULT));

export function setAssistantRailWidth(px: number): void {
  const clamped = Math.min(Math.max(Math.round(px), RAIL_MIN), railMax());
  assistantRailWidth.set(clamped);
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--assistant-rail-w', `${clamped}px`);
  }
  try {
    localStorage.setItem(RAIL_KEY, String(clamped));
  } catch {
    // As above.
  }
}

/* ── Undocked ─────────────────────────────────────────────

   The same drawer, let go of the corner: a window you move by its bar and size
   from its bottom-right corner. It is still the drawer - same conversation,
   same shrink to the mark, same Escape - only where it sits changes. Off by
   default and a setting, because a docked panel is the one people expect.
   Width and height are shared with the docked drawer; the position is its own.

   The position is stored as the top-left corner in pixels and clamped again in
   CSS against the window, so a window that has since got smaller never strands
   the bar off-screen. */

const FLOAT_KEY = 'neptune_assistant_floating_v1';
const FLOAT_AT_KEY = 'neptune_assistant_float_at_v1';

export interface FloatAt {
  x: number;
  y: number;
}

function readFloating(): boolean {
  try {
    return localStorage.getItem(FLOAT_KEY) === '1';
  } catch {
    return false;
  }
}

/** Where a window opens the first time: where the docked drawer's top-left
 *  corner would be, pulled in from the edges so it reads as a window. */
function defaultFloatAt(): FloatAt {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  const height = assistantDrawerHeight.value || drawerMaxHeight();
  return {
    x: Math.max(0, window.innerWidth - assistantDrawerWidth.value - 24),
    y: Math.max(headerHeight() + 12, window.innerHeight - height - 24),
  };
}

function readFloatAt(): FloatAt | null {
  try {
    const raw = localStorage.getItem(FLOAT_AT_KEY);
    if (!raw) return null;
    const at = JSON.parse(raw) as Partial<FloatAt>;
    if (!Number.isFinite(at.x) || !Number.isFinite(at.y)) return null;
    return { x: at.x as number, y: at.y as number };
  } catch {
    return null;
  }
}

export const assistantFloating = new Atom<boolean>(readFloating());
export const assistantFloatAt = new Atom<FloatAt>(readFloatAt() ?? defaultFloatAt());

export function setAssistantFloating(on: boolean): void {
  assistantFloating.set(on);
  // Undocking opens where the drawer already is, not where a window was left
  // weeks ago on a different screen.
  if (on) assistantFloatAt.set(defaultFloatAt());
  syncAssistantSurface();
  try {
    localStorage.setItem(FLOAT_KEY, on ? '1' : '0');
  } catch {
    // The choice still applies for this session.
  }
}

/** Move the window. Kept inside the viewport with the bar always reachable. */
export function setAssistantFloatAt(x: number, y: number): void {
  const w = typeof window === 'undefined' ? 1e4 : window.innerWidth;
  const h = typeof window === 'undefined' ? 1e4 : window.innerHeight;
  const at = {
    x: Math.round(Math.min(Math.max(x, 0), Math.max(0, w - 120))),
    y: Math.round(Math.min(Math.max(y, 0), Math.max(0, h - 44))),
  };
  assistantFloatAt.set(at);
  syncAssistantSurface();
  try {
    localStorage.setItem(FLOAT_AT_KEY, JSON.stringify(at));
  } catch {
    // Not worth failing a drag over.
  }
}

/** Whether this turn asks for Chief Engineer mode: switched on, and root. */
export function chiefEngineerOn(): boolean {
  return assistantChiefEngineer.value && isRootUser.value;
}
